import React from 'react';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import extractDialogProps from 'components/extractDialogProps';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import Collapse from '@material-ui/core/Collapse';
import TextField from '@material-ui/core/TextField';
import Divider from '@material-ui/core/Divider';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CloseIcon from '@material-ui/icons/Close';

import DateFnsUtils from '@date-io/date-fns';
import format from "date-fns/format";
import ja from 'date-fns/locale/ja'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'

import { useFormik, useField, Form, FormikProvider } from 'formik';
import useAuth from 'components/Auth/useAuth';
import FormControl from '@material-ui/core/FormControl';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
    width :'auto',
  },
  close: {
    alignSelf: "end"
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const FormTextField = React.forwardRef(function FormTextField(props, ref) {
  const [field, meta] = useField(props);

  let
    {
      helperText,
      ...rest
    } = props;

  const error = Boolean(meta.error);
  if (error) {
    helperText = meta.error;
  }

  return (
    <TextField
      ref={ref}
      error={meta.touched && meta.error}
      helperText={helperText}
      {...field}
      {...rest} />
  )
});

const FormSelect = React.forwardRef(function FormSelect(props, ref) {
  const [field, meta] = useField(props);

  let
  {
    label,
    children,
    ...rest
  } = props;

  return (
    <React.Fragment>
      <InputLabel id={field.id + "select-label"}>{label}</InputLabel>
      <Select
        ref={ref}
        labelId={field.id + "select-label"}
        error={meta.touched && meta.error}
        {...field}
        {...rest} >
          {children}
      </Select>
    </React.Fragment>
  )
});

const FormDate = React.forwardRef(function FormDate(props, ref) {
  const [field, meta, helpers] = useField(props);

  const handleChange = React.useCallback((value) => {
    if (value) {
      helpers.setValue(value);
    }
  });
  
  return (
    <KeyboardDatePicker
      disableToolbar
      ref={ref}
      format='yyyy/MM/dd'
      error={meta.touched && meta.error}
      KeyboardButtonProps={{
        'aria-label': 'change date',
      }}
      value={field.value}
      onChange={handleChange}
      onBlur={field.onBlur}
      {...props}
      />)
});

const FormCheckbox = React.forwardRef(function FormCheckbox(props, ref) {
  const [field, meta, helpers] = useField(props);

  let {
    label,
    ...rest
  } = props;

  let {
    value,
    ...field_rest
  } = field

  return (
    <FormControlLabel 
      label={label}
      control={
      <Checkbox
        ref={ref}
        checked={field.value}
        color="primary"
        {...field_rest}
        {...rest}
         />}
      />)
});

function createOption(id, name)
{
  return {id, name};
}

function viewableReportsToOptions(reports)
{
  let temp = [];
  if (reports.includes(1)) temp.push(createOption(1, '請求書'));
  if (reports.includes(2)) temp.push(createOption(2, '給与明細書'));
  if (reports.includes(12)) temp.push(createOption(12, '個別契約書'));
  if (reports.includes(13)) temp.push(createOption(13, '派遣通知書'));
  if (reports.includes(14)) temp.push(createOption(14, '抵触日通知書'));
  if (reports.includes(16)) temp.push(createOption(16, '派遣先管理台帳'));
  if (reports.includes(21) || reports.includes(22) || reports.includes(23)) temp.push(createOption(21, '雇用契約書/就業条件明示書'));
  return temp;
}

function createCriteria(auth, options) {
  let today = new Date();
  today.setHours(0, 0, 0);
  let oneMonthBefore = new Date(today);
  oneMonthBefore.setMonth(today.getMonth() - 1);

  return {
    agencyName: "",
    clientName: "",
    report: auth.report ?? options.length > 0 ? options[0].id : "",
    setSendDate: false,
    sendDateBegin: oneMonthBefore,
    sendDateEnd: today,
    setBillDate: false,
    billDateBegin: oneMonthBefore,
    billDateEnd: today,
    setBillMoney: false,
    billMoneyBegin: "",
    billMoneyEnd: "",
    checkVoid: false,
    setSalaryPaymentDate: false,
    salaryPaymentDateBegin: oneMonthBefore,
    salaryPaymentDateEnd: today,
    setSalaryClosedDate: false,
    salaryClosedDateBegin: oneMonthBefore,
    salaryClosedDateEnd: today,
  };
}

class ExtendDateFnsUtils extends DateFnsUtils {
  getCalendarHeaderText(date) {
    return format(date, 'yyyy年 MMM', { locale: this.locale });
  }
}

export default function SearchDialog(props) {
  const [dialogProps] = extractDialogProps(props);

  const {
    open,
    criteria,
    setCriteria,
    handleClose,
    viewableReports
  } = props;

  const auth = useAuth();
  const options = viewableReportsToOptions(viewableReports);

  const handleSearch = React.useCallback((values) => {
    setCriteria(values);
    handleClose();
  }, [setCriteria]);

  const formikBag = useFormik(
    {
      initialValues: criteria ?? createCriteria(auth, options),
      onSubmit: handleSearch
    });

  const classes = useStyles();
  const downXs = useMediaQuery(theme => theme.breakpoints.down("xs"));

  return (
    <div>
      <Dialog
        {...dialogProps}
        open={open}
        aria-labelledby="search-dialog-title"
        aria-describedby="search-dialog-description"
        fullScreen={downXs}
      >
      {downXs ? <IconButton onClick={handleClose} className={classes.close}><CloseIcon /></IconButton> : null }
      <Container maxWidth="xs" className={classes.paper}>
        <FormikProvider value={formikBag}>
          <Form className={classes.form}>
            <MuiPickersUtilsProvider utils={ExtendDateFnsUtils} locale={ja}>
            <FormSelect
              variant="outlined"
              required
              id="report"
              name="report"
              label="帳票種別"
              fullWidth>
              {options.map(option => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
            </FormSelect>
            <Divider className={classes.divider} />
            <FormTextField
              variant="outlined"
              id="agencyName"
              name="agencyName"
              label="企業名"
              fullWidth />
            <Collapse in={formikBag.values.report == 21} >
              <div style={{height:'10px'}} />
              <FormTextField
                variant="outlined"
                id="clientName"
                name="clientName"
                label="就業先名"
                fullWidth />
            </Collapse>
            <Divider className={classes.divider} />
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <FormCheckbox
                  label="送付日を絞り込む"
                  id="setSendDate"
                  name="setSendDate"
                />
              </Grid>
              {downXs ?
                <Grid item xs={3}>
                  <Box mx="auto" p={1}>
                    <Typography>開始：</Typography>
                  </Box>
                </Grid> : null
              }
              <Grid item xs={9} sm={5}>
                <FormDate 
                  id="sendDateBegin"
                  name="sendDateBegin"
                  disabled={!formikBag.values.setSendDate}
                />
              </Grid>
              {downXs ? null : 
                <Grid item sm={2}>
                  <Box mx="auto" p={1}>
                    <Typography>～</Typography>
                  </Box>
                </Grid>
              }
              {downXs ?
                <Grid item xs={3}>
                  <Box mx="auto" p={1}>
                    <Typography>終了：</Typography>
                  </Box>
                </Grid> : null
              }
              <Grid item xs={9} sm={5}>
                <FormDate 
                  id="sendDateEnd"
                  name="sendDateEnd"
                  disabled={!formikBag.values.setSendDate}
                />
              </Grid>
              </Grid>
              <Collapse in={formikBag.values.report == 1} >
                <Divider className={classes.divider} />
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <FormCheckbox
                      label="請求締日を絞り込む"
                      id="setBillDate"
                      name="setBillDate"
                    />
                  </Grid>
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>開始：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormDate 
                      id="billDateBegin"
                      name="billDateBegin"
                      disabled={!formikBag.values.setBillDate}
                    />
                  </Grid>
                  {downXs ? null : 
                    <Grid item sm={2}>
                      <Box mx="auto" p={1}>
                        <Typography>～</Typography>
                      </Box>
                    </Grid>
                  }
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>終了：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormDate 
                      id="billDateEnd"
                      name="billDateEnd"
                      disabled={!formikBag.values.setBillDate}
                    />
                  </Grid>
                </Grid>
                <Divider className={classes.divider} />
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <FormCheckbox
                      label="請求金額を絞り込む"
                      id="setBillMoney"
                      name="setBillMoney"
                    />
                  </Grid>
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>開始：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormTextField 
                      id="billMoneyBegin"
                      name="billMoneyBegin"
                      type="number"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">￥</InputAdornment>,
                      }}
                      disabled={!formikBag.values.setBillMoney}
                    />
                  </Grid>
                  {downXs ? null : 
                    <Grid item sm={2}>
                      <Box mx="auto" p={1}>
                        <Typography>～</Typography>
                      </Box>
                    </Grid>
                  }
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>終了：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormTextField 
                      id="billMoneyEnd"
                      name="billMoneyEnd"
                      type="number"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">￥</InputAdornment>,
                      }}
                      disabled={!formikBag.values.setBillMoney}
                    />
                  </Grid>
                </Grid>
              </Collapse>
              <Collapse in={formikBag.values.report == 2} >
                <Divider className={classes.divider} />
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <FormCheckbox
                      label="給与支払日を絞り込む"
                      id="setSalaryPaymentDate"
                      name="setSalaryPaymentDate"
                    />
                  </Grid>
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>開始：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormDate
                      id="salaryPaymentDateBegin"
                      name="salaryPaymentDateBegin"
                      disabled={!formikBag.values.setSalaryPaymentDate}
                    />
                  </Grid>
                  {downXs ? null :
                    <Grid item sm={2}>
                      <Box mx="auto" p={1}>
                        <Typography>～</Typography>
                      </Box>
                    </Grid>
                  }
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>終了：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormDate
                      id="salaryPaymentDateEnd"
                      name="salaryPaymentDateEnd"
                      disabled={!formikBag.values.setSalaryPaymentDate}
                    />
                  </Grid>
                </Grid>
                <Divider className={classes.divider} />
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <FormCheckbox
                      label="給与締日を絞り込む"
                      id="setSalaryClosedDate"
                      name="setSalaryClosedDate"
                    />
                  </Grid>
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>開始：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormDate
                      id="salaryClosedDateBegin"
                      name="salaryClosedDateBegin"
                      disabled={!formikBag.values.setSalaryClosedDate}
                    />
                  </Grid>
                  {downXs ? null :
                    <Grid item sm={2}>
                      <Box mx="auto" p={1}>
                        <Typography>～</Typography>
                      </Box>
                    </Grid>
                  }
                  {downXs ?
                    <Grid item xs={3}>
                      <Box mx="auto" p={1}>
                        <Typography>終了：</Typography>
                      </Box>
                    </Grid> : null
                  }
                  <Grid item xs={9} sm={5}>
                    <FormDate
                      id="salaryClosedDateEnd"
                      name="salaryClosedDateEnd"
                      disabled={!formikBag.values.setSalaryClosedDate}
                    />
                  </Grid>
                </Grid>
              </Collapse>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className={classes.submit}
              >
                検索
              </Button>
            </MuiPickersUtilsProvider>
          </Form>
        </FormikProvider>
        </Container>
      </Dialog>
    </div>
  );
}